export type NodeSearchSubAttr = {
    attr: string;
    field: string;
};

export type NodeReplaceableAttr = {
    attr: string;
    replaceable: boolean;
};

export type NodeSearchTarget = {
    /*
     * A list of attributes to search in the node and whether they are replaceable.
     */
    attr_and_replaceable: NodeReplaceableAttr[];

    /*
     * Optional. A list of sub-attributes to search in the node. For nodes that have nested attributes, like 'zyDE' with 'multifiles' attribute.
     */
    subAttrs?: NodeSearchSubAttr[];

    /*
     * Whether the search results within the node should be limited to one match.
     */
    maxOneMatch: boolean;

    /*
     * Whether the entire node should be highlighted when a search result is found.
     */
    highlightEntireNode: boolean;

    /*
     * Optional. Whether the text search should be skipped for the node. For nodes like 'zyConsole' that have text content, but no attributes to search in.å
     */
    skipTextSearch?: boolean;
};

export const NODE_SEARCH_TARGETS = new Map<string, NodeSearchTarget>([
    [
        'inlineMath',
        {
            attr_and_replaceable: [{ attr: 'latex', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: true,
        },
    ],

    [
        'blockMath',
        {
            attr_and_replaceable: [{ attr: 'latex', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: true,
        },
    ],

    [
        'questionSet',
        {
            attr_and_replaceable: [{ attr: 'caption', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'shortAnswerAnswer',
        {
            attr_and_replaceable: [{ attr: 'answers', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'shortAnswerAnswerWrapper',
        {
            attr_and_replaceable: [{ attr: 'content', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'zyAside',
        {
            attr_and_replaceable: [
                { attr: 'caption', replaceable: true },
                { attr: 'type', replaceable: false },
            ],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyExample',
        {
            attr_and_replaceable: [
                { attr: 'caption', replaceable: true },
                { attr: 'enumType', replaceable: true },
            ],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyExercise',
        {
            attr_and_replaceable: [{ attr: 'caption', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyYoutubeVideo',
        {
            attr_and_replaceable: [{ attr: 'caption', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyStudio',
        {
            attr_and_replaceable: [
                { attr: 'caption', replaceable: true },
                { attr: 'activityType', replaceable: false },
                { attr: 'guid', replaceable: true },
            ],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyITLab',
        {
            attr_and_replaceable: [{ attr: 'caption', replaceable: true }],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'image',
        {
            attr_and_replaceable: [
                { attr: 'src', replaceable: false },
                { attr: 'alt', replaceable: false },
            ],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'zyInlineImage',
        {
            attr_and_replaceable: [
                { attr: 'src', replaceable: false },
                { attr: 'alt', replaceable: false },
            ],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'zyGdocDrawing',
        {
            attr_and_replaceable: [{ attr: 'alt_text', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'iframe',
        {
            attr_and_replaceable: [{ attr: 'src', replaceable: true }],
            maxOneMatch: true,
            highlightEntireNode: false,
        },
    ],

    [
        'zyDE',
        {
            attr_and_replaceable: [
                { attr: 'caption', replaceable: true },
                { attr: 'activityType', replaceable: false },
                { attr: 'language', replaceable: false },
            ],
            subAttrs: [{ attr: 'multifiles', field: 'program' }],
            maxOneMatch: false,
            highlightEntireNode: false,
        },
    ],

    [
        'zyConsole',
        {
            attr_and_replaceable: [],
            maxOneMatch: true,
            highlightEntireNode: true,
            skipTextSearch: true,
        },
    ],

    [
        'zyCode',
        {
            attr_and_replaceable: [],
            maxOneMatch: true,
            highlightEntireNode: true,
            skipTextSearch: true,
        },
    ],

    [
        'zyAnimator',
        {
            attr_and_replaceable: [{ attr: 'animXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'progressionBuilder',
        {
            attr_and_replaceable: [{ attr: 'progressionXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'blockOrderingBuilder',
        {
            attr_and_replaceable: [{ attr: 'blockOrderingXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'codeWritingBuilder',
        {
            attr_and_replaceable: [{ attr: 'codeWritingXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'codeOutputBuilder',
        {
            attr_and_replaceable: [{ attr: 'codeOutputXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'parsonsCodingBuilder',
        {
            attr_and_replaceable: [{ attr: 'parsonsCodingXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'openStaxPythonCodeRunnerBuilder',
        {
            attr_and_replaceable: [{ attr: 'openStaxPythonCodeRunnerXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],

    [
        'zyTool',
        {
            attr_and_replaceable: [{ attr: 'toolXMLToLoad', replaceable: false }],
            maxOneMatch: true,
            highlightEntireNode: true,
        },
    ],
]);

export const SKIP_TEXT_SEARCH_NODE_TYPE_SET = new Set(
    [...NODE_SEARCH_TARGETS.entries()].filter(([, target]) => target.skipTextSearch).map(([name]) => name),
);
